import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { env } from '@/env';
import { authStore } from '@/store/auth';

// PartApp API envelope: HTTP is always 200; logical status lives on body.status (0|1).
export interface ApiEnvelope<T = unknown> {
  status: 0 | 1;
  message: string;
  data?: T;
  // Some endpoints return additional top-level fields (e.g. `tester`); keep loose.
  [k: string]: unknown;
}

const PAUSE_REASON_KEY = 'partapp.pauseReason';

/** The reason off the most recent 423, for the paused page to display. */
export function readPauseReason(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(PAUSE_REASON_KEY);
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public payload?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

function buildClient(): AxiosInstance {
  const c = axios.create({
    baseURL: env.apiBaseUrl,
    timeout: 30_000,
    // Send/receive cookies cross-origin so Sanctum SPA cookie auth works.
    // Axios auto-reads XSRF-TOKEN cookie and sends it as X-XSRF-TOKEN header.
    withCredentials: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
  });

  c.interceptors.request.use((config) => {
    // Bearer is now only kept as a fallback for any legacy localStorage token
    // left over from before the cookie migration. Fresh logins do not store
    // a token; the session cookie does the work.
    const token = authStore.getState().token;
    config.headers = config.headers ?? {};
    if (token) config.headers.Authorization = `Bearer ${token}`;
    config.headers['X-App-Platform'] = 'web';
    // Lets the API record which console build this user is on, alongside the
    // Android and iOS versions it already tracks.
    config.headers['X-App-Version'] = __APP_VERSION__;
    config.headers['Accept'] = 'application/json';
    if (env.mobileAppToken) config.headers['MOBILE-APP-TOKEN'] = env.mobileAppToken;
    return config;
  });

  c.interceptors.response.use(
    (res) => res,
    (err: AxiosError<ApiEnvelope>) => {
      // 423 — a super admin has paused this academy. Every admin and manager
      // route answers it, so there is nothing to retry and no partial state to
      // keep; the whole console is replaced by the explanation.
      if (err.response?.status === 423) {
        const reason = (err.response.data as { pause_reason?: unknown } | undefined)?.pause_reason;
        // Handed over out of band rather than through router state: the 423
        // can come from any in-flight query, none of which is a navigation.
        if (typeof window !== 'undefined') {
          try {
            if (typeof reason === 'string' && reason.trim()) {
              sessionStorage.setItem(PAUSE_REASON_KEY, reason);
            } else {
              sessionStorage.removeItem(PAUSE_REASON_KEY);
            }
          } catch {
            // Private-mode browsers throw on sessionStorage; the page just
            // renders without the reason.
          }
          if (!location.pathname.startsWith('/academy-paused')) {
            location.assign('/academy-paused');
          }
        }
      }

      if (err.response?.status === 401) {
        authStore.getState().clear();
        if (typeof window !== 'undefined' && !location.pathname.startsWith('/login')) {
          location.assign('/login');
        }
      }
      const msg = err.response?.data?.message ?? err.message ?? 'Network error';
      return Promise.reject(new ApiError(msg, err.response?.status ?? 0, err.response?.data));
    },
  );

  return c;
}

export const apiClient = buildClient();

/**
 * Fetches the Sanctum CSRF cookie. Must be called BEFORE any state-changing
 * request that targets a cookie-auth endpoint (login, logout). Sets the
 * XSRF-TOKEN cookie which axios then forwards as X-XSRF-TOKEN automatically.
 *
 * Sanctum exposes this at the host root (not under /api), so we derive the
 * URL from apiBaseUrl by stripping any path segment.
 */
export async function fetchCsrfCookie(): Promise<void> {
  const csrfUrl = new URL('/sanctum/csrf-cookie', env.apiBaseUrl).toString();
  await axios.get(csrfUrl, { withCredentials: true });
}

// Convenience helper that unwraps the envelope and throws on `status: 0`.
export async function apiCall<T = unknown>(config: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.request<ApiEnvelope<T>>(config);
  if (res.data.status === 0) {
    throw new ApiError(res.data.message || 'Request failed', res.status, res.data);
  }
  return (res.data.data ?? (res.data as unknown as T)) as T;
}
